import {ActivityReview} from '@/containers';
import {ProgressIndicator, HeaderTitle, Breadcrumb} from '@/components/atoms';
import DuplicateAlert from '@/components/atoms/Toast/DuplicateAlert';
import {ROUTES} from '@/utils/constants/routesConstants';
import {useNavigate, useParams, useSearchParams} from 'react-router-dom';
import useActivityReview from '@/containers/Activity/ActivityReview/ActivityReview-hook';
import useActivityReviewActions from '@/containers/Activity/ActivityReview/hooks/useActivityReviewActions';
import {getModal} from '@/utils/CommonModal';
import {useAppSelector} from '@/store';
import {useMemo} from 'react';
import {Constants} from '@/utils/constants/commonConstants';
import {
  ACTIVITY_TYPE,
  isVideoActivityType,
  isWheelOfFortuneActivityType,
  isFreeKickActivityType,
  isBackgammonActivityType
} from '@/types/Activity';
import ActivityReviewActionButtons from '@/containers/Activity/ActivityReview/components/ActivityReviewActionButtons';

const AddActivityReview = () => {
  const navigate = useNavigate();
  const {id} = useParams();
  const [searchParams] = useSearchParams();
  const activityFormData = useAppSelector((state) => state.activity.formData);

  const reviewParam = searchParams.get('review');
  const isReviewCompleted =
    reviewParam?.toLowerCase() === Constants.ReviewCompleted.toLowerCase();
  const showProgressIndicator = !id;

  const {handleCancel, onClickPrimaryButton, onClickSecondaryButton, isCancel} =
    useActivityReview();

  // Dynamic breadcrumb logic
  const breadcrumbs = useMemo(() => {
    const baseBreadcrumbs = [
      {label: '', href: ROUTES.HOME},
      {label: 'Loyalty Campaigns', href: ROUTES.ACTIVITIES}
    ];

    // Check if we have an existing activity (either from URL id or data id/uuid)
    const hasExistingActivity =
      id || activityFormData?.uuid || activityFormData?.id;
    const activityName = activityFormData?.activityName;

    // Creation flow - no id in URL and no existing data
    if (!hasExistingActivity) {
      return [
        ...baseBreadcrumbs,
        {label: 'Add Activity', href: ROUTES.ACTIVITIES}
      ];
    }

    // Review/Edit flow - id exists in URL or data has id/uuid
    const displayName = activityName || 'Loading...';
    return [
      ...baseBreadcrumbs,
      {label: 'Activity', href: ROUTES.ACTIVITIES},
      {label: displayName, href: ROUTES.ACTIVITIES}
    ];
  }, [
    id,
    activityFormData?.activityName,
    activityFormData?.uuid,
    activityFormData?.id
  ]);

  const isSurveyFlow = activityFormData?.activityType === ACTIVITY_TYPE.SURVEY;
  const isVideoFlow = isVideoActivityType(activityFormData?.activityType);
  const isWheelOfFortuneFlow = isWheelOfFortuneActivityType(
    activityFormData?.activityType
  );
  const isFreeKickFlow = isFreeKickActivityType(activityFormData?.activityType);
  const isBackgammonFlow = isBackgammonActivityType(
    activityFormData?.activityType
  );
  const isThreeStepFlow =
    isSurveyFlow ||
    isVideoFlow ||
    isWheelOfFortuneFlow ||
    isFreeKickFlow ||
    isBackgammonFlow;
  const isReviewViewMode =
    !!id &&
    (isSurveyFlow ||
      isVideoFlow ||
      isWheelOfFortuneFlow ||
      isFreeKickFlow ||
      isBackgammonFlow);

  let activityKind = null;
  if (isSurveyFlow) {
    activityKind = 'survey';
  } else if (isVideoFlow) {
    activityKind = 'video';
  } else if (isWheelOfFortuneFlow) {
    activityKind = 'wheelOfFortune';
  } else if (isFreeKickFlow) {
    activityKind = 'freeKick';
  } else if (isBackgammonFlow) {
    activityKind = 'backgammon';
  }

  const reviewActions = useActivityReviewActions(activityKind);

  const pageTitle = useMemo(() => {
    if (!id) return 'Activity Review';
    if (isSurveyFlow) {
      return activityFormData?.activityName || 'Survey Review';
    }
    if (isVideoFlow) {
      return activityFormData?.activityName || 'Video Review';
    }
    return 'Activity Review';
  }, [id, isSurveyFlow, isVideoFlow, activityFormData?.activityName]);

  const handleStepClick = (step) => {
    let flowType = 'default';
    if (isWheelOfFortuneFlow) flowType = 'wheel';
    else if (isSurveyFlow) flowType = 'survey';
    else if (isVideoFlow) flowType = 'video';
    else if (isFreeKickFlow) flowType = 'freeKick';
    else if (isBackgammonFlow) flowType = 'backgammon';
    const routeMap = {
      wheel: {
        1: ROUTES.ACTIVITY_ADD,
        2: ROUTES.ACTIVITY_DISPLAY,
        3: ROUTES.ACTIVITY_ATTRIBUTES
      },
      survey: {1: ROUTES.ACTIVITY_ADD, 2: ROUTES.ACTIVITY_ATTRIBUTES},
      video: {1: ROUTES.ACTIVITY_ADD, 2: ROUTES.ACTIVITY_DISPLAY},
      freeKick: {
        1: ROUTES.ACTIVITY_ADD,
        2: ROUTES.ACTIVITY_DISPLAY,
        3: ROUTES.ACTIVITY_ATTRIBUTES
      },
      backgammon: {
        1: ROUTES.ACTIVITY_ADD,
        2: ROUTES.ACTIVITY_DISPLAY,
        3: ROUTES.ACTIVITY_ATTRIBUTES
      },
      default: {1: ROUTES.ACTIVITY_ADD}
    };

    const route = routeMap[flowType]?.[step];
    if (route) navigate(route);
  };

  const getCancelModal = () => {
    return getModal({
      title: 'Are you sure you want to cancel?',
      text: 'Your work will not be saved if you click Yes. To continue editing and go back to the page, click NO',
      showPrimaryButton: true,
      primaryButtonText: 'Yes',
      onClickPrimaryButton: onClickPrimaryButton,
      showSecondaryButton: true,
      secondaryButtonText: 'No',
      onClickSecondaryButton: onClickSecondaryButton,
      showModal: isCancel,
      onCloseModal: handleCancel,
      hideCloseButton: false
    });
  };

  let progressStepCount;
  if (isWheelOfFortuneFlow || isFreeKickFlow || isBackgammonFlow) {
    progressStepCount = 4;
  } else if (isThreeStepFlow) {
    progressStepCount = 3;
  } else {
    progressStepCount = 2;
  }

  let progressTitles;
  if (isWheelOfFortuneFlow || isFreeKickFlow || isBackgammonFlow) {
    progressTitles = [
      {title: 'Setup'},
      {title: 'Display'},
      {title: 'Attributes'},
      {title: 'Review'}
    ];
  } else if (isSurveyFlow) {
    progressTitles = [
      {title: 'Setup'},
      {title: 'Attributes'},
      {title: 'Review'}
    ];
  } else if (isVideoFlow) {
    progressTitles = [{title: 'Setup'}, {title: 'Display'}, {title: 'Review'}];
  } else {
    progressTitles = [{title: 'Setup'}, {title: 'Review'}];
  }

  return (
    <>
      <Breadcrumb
        uiSchema={{
          isDark: true,
          className: 'mb-5',
          breadcrumbs: breadcrumbs
        }}
        id={'aar_bradcrumb'}
      />
      <HeaderTitle
        title={pageTitle}
        className='mb-3 justify-start text-3xl font-bold text-slate-200'
      />
      {isReviewViewMode && reviewActions.actionAlert && (
        <DuplicateAlert
          type={reviewActions.actionAlert.type}
          message={{
            title: reviewActions.actionAlert.title,
            subTitle: reviewActions.actionAlert.subTitle
          }}
          show={true}
          onCloseClick={reviewActions.handleDismissAlert}
        />
      )}
      {isReviewViewMode && activityKind && (
        <div className='flex justify-end'>
          <ActivityReviewActionButtons
            reviewActions={reviewActions}
            activityKind={activityKind}
          />
        </div>
      )}
      {showProgressIndicator && !isReviewCompleted && (
        <ProgressIndicator
          currentStep={progressStepCount}
          completed={false}
          steps={progressStepCount}
          titles={progressTitles}
          className='mb-6'
          onStepClick={handleStepClick}
        />
      )}
      <br />
      <ActivityReview
        cancelModalHandlers={{
          handleCancel,
          onClickPrimaryButton,
          onClickSecondaryButton,
          isCancel
        }}
        isReviewViewMode={isReviewViewMode}
      />
      {isCancel && getCancelModal()}
    </>
  );
};

export default AddActivityReview;
